import { useState } from 'react';
import { apiFundsFlowService, TFundsFlowCurrencyBlock, TFundsFlowResponse } from '@api';
import { ETransactionStatus } from '@xeppt/xeppt-sdk/types/transaction';
import { useApiQuery } from '@hooks/api/useApiQuery';

export const DEFAULT_FUNDS_FLOW_RANGE_DAYS = 30;
export const MAX_FUNDS_FLOW_RANGE_DAYS = 366;

const DAY_MS = 24 * 60 * 60 * 1000;

export type TFundsFlowFilter = {
    date_from: Date;
    date_to: Date;
    statuses: number[];
};

export const getDefaultDateRange = (): { date_from: Date; date_to: Date } => {
    const date_to = new Date();
    const date_from = new Date(date_to);
    date_from.setDate(date_from.getDate() - DEFAULT_FUNDS_FLOW_RANGE_DAYS);
    return { date_from, date_to };
};

export type TDateRangeValidation = { valid: true } | { valid: false; message: string };

// Mirrors the backend's own validation (date_from < date_to, range <= 366 days) so the
// admin gets an inline message instead of waiting on a 400 round-trip.
export const validateDateRange = (date_from?: Date, date_to?: Date): TDateRangeValidation => {
    if (!date_from || !date_to) {
        return { valid: false, message: 'Select a start and end date.' };
    }
    if (date_from.getTime() >= date_to.getTime()) {
        return { valid: false, message: 'The start date must be before the end date.' };
    }
    const spanDays = (date_to.getTime() - date_from.getTime()) / DAY_MS;
    if (spanDays > MAX_FUNDS_FLOW_RANGE_DAYS) {
        return {
            valid: false,
            message: `The date range can't be longer than ${MAX_FUNDS_FLOW_RANGE_DAYS} days.`
        };
    }
    return { valid: true };
};

const serializeFilter = (filter: TFundsFlowFilter) => ({
    date_from: filter.date_from.toISOString(),
    date_to: filter.date_to.toISOString(),
    statuses: filter.statuses
});

export const useFundsFlow = () => {
    const [filter, setFilter] = useState<TFundsFlowFilter>(() => ({
        ...getDefaultDateRange(),
        statuses: [ETransactionStatus.APPROVED]
    }));
    const [validationError, setValidationError] = useState<string>();
    const [selectedCurrency, setSelectedCurrency] = useState<string>();

    const { data, isLoading, error, handleRequest } = useApiQuery<
        TFundsFlowFilter,
        TFundsFlowResponse
    >({
        method: (request) => apiFundsFlowService.getFundsFlow(serializeFilter(request ?? filter)),
        data: filter,
        isInitialRequest: true,
        onSuccess: (result) => {
            // Keep the current tab if it still exists in the new response (e.g. after a
            // filter change); otherwise fall back to the highest-volume currency.
            setSelectedCurrency((current) =>
                current && result.currencies.some((block) => block.currency === current)
                    ? current
                    : result.currencies[0]?.currency
            );
        }
    });

    const onChangeFilter = (next: Partial<TFundsFlowFilter>) => {
        const merged = { ...filter, ...next };
        setFilter(merged);
        const validation = validateDateRange(merged.date_from, merged.date_to);
        if (!validation.valid) {
            setValidationError(validation.message);
            return;
        }
        setValidationError(undefined);
        handleRequest(merged);
    };

    const currencies = data?.currencies ?? [];
    const selectedCurrencyBlock: TFundsFlowCurrencyBlock | undefined =
        currencies.find((block) => block.currency === selectedCurrency) ?? currencies[0];

    return {
        filter,
        onChangeFilter,
        validationError,
        isLoading,
        error,
        currencies,
        selectedCurrency: selectedCurrencyBlock?.currency,
        setSelectedCurrency,
        selectedCurrencyBlock
    };
};
